import "./About.css";
import { TEAM_DETAILS } from "../../../utils/constants";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getRoutePaths, useLang } from "../../../utils/i18n";
import ClientLogos from "../../ClientLogos/ClientLogos";

const About = () => {

    const lang = useLang();
    const paths = getRoutePaths(lang);
    const teamTeasers = lang === "en"
      ? [
          "Luciana helps businesses strengthen their financial structure, governance and strategic decision-making.",
          "Gabrielle turns financial planning and performance insight into clearer, more confident executive decisions.",
          "Gabriel builds reliable financial operations that give leaders clarity, control and confidence to grow."
        ]
      : [
          "Luciana ajuda empresas a fortalecer sua estrutura financeira, governança e tomada de decisões estratégicas.",
          "Gabrielle transforma planejamento financeiro e análises de performance em decisões executivas mais claras e seguras.",
          "Gabriel constrói operações financeiras confiáveis que dão aos líderes clareza, controle e confiança para crescer."
        ];
    const [teamDetailsData, setTeamDetailsData] = useState(
        TEAM_DETAILS[lang].map((member) => ({ ...member, showFullBio: false }))
    );

    useEffect(() => {
        setTeamDetailsData(TEAM_DETAILS[lang].map((member) => ({ ...member, showFullBio: false })));
    }, [lang]);

    const toggleReadMore = (index) => {
        setTeamDetailsData((prevDetails) =>
            prevDetails.map((member, memberIndex) => {
                if (memberIndex !== index) {
                    return member;
                }
                return { ...member, showFullBio: !member.showFullBio };
            })
        );
    };

    const pillars = lang === "en"
      ? [
          {
            title: "Our Mission",
            description:
              "Turning financial insight into better decisions. Financial data only creates value when it helps people make better decisions. Finance should move the business forward."
          },
          {
            title: "Our Vision",
            description:
              "A more strategic, human and connected finance function. We help develop businesses and professionals capable of using Finance as a true strategic partner."
          },
          {
            title: "What We Believe",
            description:
              "Clarity over complexity. Decisions, not just reports. People behind the numbers. Technology with purpose. Growth that lasts."
          }
        ]
      : [
          {
            title: "Nossa Missão",
            description:
              "Transformar informação financeira em melhores decisões. Dados financeiros só geram valor quando ajudam pessoas a tomar melhores decisões. O financeiro deve impulsionar o negócio."
          },
          {
            title: "Nossa Visão",
            description:
              "Um financeiro mais estratégico, humano e conectado ao negócio. Contribuímos para desenvolver empresas e profissionais capazes de utilizar o financeiro como um verdadeiro parceiro estratégico."
          },
          {
            title: "O que acreditamos",
            description:
              "Clareza antes da complexidade. Decisões, não apenas relatórios. Pessoas por trás dos números. Tecnologia com propósito. Crescimento que permanece."
          }
        ];

  return (
    <section className="about-page">
      <h1 className="main-heading">
        {lang === "en" ? "About Us" : "Sobre Nós, Quem Somos"}
      </h1>
      <div className="about-description">
        <div className="about-intro">
          <span className="about-kicker">{lang === "en" ? "Our perspective" : "Nossa perspectiva"}</span>
          <p className="about-lead">
            {lang === "en"
              ? "Finance goes far beyond numbers. It is the language of strategy."
              : "Finanças vão muito além dos números. São a linguagem da estratégia."}
          </p>
          <div className="about-signature" aria-hidden="true"><span>M</span></div>
        </div>
        <div className="about-copy">
          <p>
            {lang === "en"
              ? "We are a collective of thinkers, analysts and builders who help organisations turn complexity into clarity — and clarity into results that truly matter."
              : "Somos um coletivo de pensadores, analistas e criadores que ajuda organizações a transformar complexidade em clareza — e clareza em resultados que realmente importam."}
          </p>
          <p>
            {lang === "en"
              ? "Our work sits at the intersection of finance, data, leadership and technology. Whether building financial models, advising executives, modernising systems or supporting global expansion, our focus remains the same: creating lasting value."
              : "Nosso trabalho acontece na interseção entre finanças, dados, liderança e tecnologia. Seja desenvolvendo modelos financeiros, orientando executivos, modernizando sistemas ou apoiando expansões globais, nosso foco permanece o mesmo: criar valor duradouro."}
          </p>
          <p>
            {lang === "en"
              ? "We listen, analyse and challenge. Every strategy is shaped around the people, ambitions and realities of the organisation in front of us."
              : "Ouvimos, analisamos e questionamos. Cada estratégia é moldada pelas pessoas, ambições e realidades da organização à nossa frente."}
          </p>
          <p className="about-closing">
            {lang === "en"
              ? "Insight into action. Ambition into lasting value."
              : "Insight em ação. Ambição em valor duradouro."}
          </p>
        </div>
      </div>
      <section className="about-pillars" aria-label={lang === "en" ? "Core pillars" : "Pilares principais"}>
        {pillars.map((pillar, index) => (
          <article className="about-pillar" key={pillar.title}>
            <span className="about-pillar-index">{`0${index + 1}`}</span>
            <h2 className="about-pillar-title">{pillar.title}</h2>
            <p className="about-pillar-text">{pillar.description}</p>
            {index === 2 && (
              <Link className="about-pillar-link" to={paths.beliefs}>
                {lang === "en" ? "Explore our principles" : "Conheça nossos princípios"}
              </Link>
            )}
          </article>
        ))}
      </section>
      <header className="team-header">
        <span className="team-header-kicker">
          {lang === "en" ? "Our people" : "Nosso time"}
        </span>
        <h2 className="team-header-title">
          {lang === "en" ? "Meet the people behind Monetique" : "Conheça as pessoas por trás da Monetique"}
        </h2>
        <p className="team-header-text">
          {lang === "en"
            ? "Experienced leaders bringing finance, strategy and execution together."
            : "Líderes experientes que unem finanças, estratégia e execução."}
        </p>
      </header>
      <section className="team-grid" aria-label={lang === "en" ? "Leadership team" : "Equipe de liderança"}>
        {teamDetailsData.map((member, index) => (
          <article className="team-member" key={`${member.name}-${index}`}>
            <div className="team-member-media">
              <img
                src={member.image}
                alt={`${member.name}, ${member.role}`}
                className="team-member-image"
                loading="lazy"
                width="400"
                height="400"
              />
            </div>
            <div className="team-member-caption">
              <h3 className="team-member-name">{member.name}</h3>
              <h4 className="team-member-role">
                <span>{member.role.split("|")[0].trim()}</span>
                {member.role.includes("|") && (
                  <span className="team-member-specialty">{member.role.split("|").slice(1).join("|").trim()}</span>
                )}
              </h4>
            </div>
            <p className="team-member-bio">
                {member.bio.length > 145 && !member.showFullBio ? (
                  <>
                    {teamTeasers[index]}
                    {" "}
                    <button
                      className="read-more-button is-expand"
                      type="button"
                      onClick={() => toggleReadMore(index)}
                    >
                      {lang === "en" ? "Read more" : "Ver mais"}
                    </button>
                  </>
                ) : (
                  <>
                    {member.bio}
                    {member.bio.length > 145 && (
                      <>
                        {" "}
                        <button
                          className="read-more-button is-collapse"
                          type="button"
                          onClick={() => toggleReadMore(index)}
                        >
                          {lang === "en" ? "Read less" : "Ver menos"}
                        </button>
                      </>
                    )}
                  </>
                )}
            </p>
          </article>
        ))}
      </section>
      <ClientLogos />
      <section className="about-cta">
        <div className="about-cta-heading">
          <span className="about-cta-kicker">
            {lang === "en" ? "Start a conversation" : "Vamos conversar"}
          </span>
          <h2 className="about-cta-title">
            {lang === "en"
              ? "Ready to turn financial clarity into meaningful growth?"
              : "Pronto para transformar clareza financeira em crescimento real?"}
          </h2>
        </div>
        <div className="about-cta-action">
          <p className="about-cta-text">
            {lang === "en"
              ? "Tell us where you want to go. We will help you build the path forward."
              : "Conte-nos onde você quer chegar. Nós ajudamos a construir o caminho."}
          </p>
          <Link className="about-cta-link" to={paths.contact}>
            {lang === "en" ? "Contact us" : "Entre em contato"}
          </Link>
        </div>
      </section>
    </section>
  );
};

export default About;
